import React, { useEffect } from 'react'
import { cn } from '../../lib/utils'
import { IconSolid } from './Icon'
import { Button } from './Button'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showCopyButton?: boolean
  copyContent?: string
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'lg',
  showCopyButton = false,
  copyContent,
}) => {
  const [copied, setCopied] = React.useState(false)

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const handleCopy = async () => {
    if (copyContent) {
      try {
        await navigator.clipboard.writeText(copyContent)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy:', err)
      }
    }
  }

  if (!isOpen) return null

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-[95vw] h-[95vh]',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        className={cn(
          'relative bg-card rounded-2xl shadow-2xl w-full overflow-hidden animate-scale-in',
          sizeClasses[size],
          size === 'full' && 'flex flex-col'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <h2 className="text-2xl font-bold text-foreground">
            {title || 'Prompt Template'}
          </h2>
          <div className="flex items-center gap-2">
            {showCopyButton && copyContent && (
              <Button
                onClick={handleCopy}
                variant="ghost"
                size="sm"
                className="flex items-center gap-2"
              >
                <IconSolid name={copied ? 'check' : 'copy'} size="sm" />
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Close modal"
            >
              <IconSolid name="times" size="lg" className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={cn(
          'p-6 overflow-y-auto',
          size === 'full' && 'flex-1'
        )}>
          {children}
        </div>
      </div>
    </div>
  )
}

// Specialized Modal for Code/Prompt Display
export interface PromptModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  prompt: string
  description?: string
  tags?: string[]
}

export const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  onClose,
  title,
  prompt,
  description,
  tags,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="full"
      showCopyButton
      copyContent={prompt}
    >
      <div className="space-y-6">
        {/* Description */}
        {description && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600 p-4 rounded-r-lg">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {description}
            </p>
          </div>
        )}

        {/* Tags */}
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Prompt Content */}
        <div className="bg-slate-900 text-cyan-400 p-6 rounded-lg font-mono text-sm overflow-x-auto">
          <pre className="whitespace-pre-wrap leading-relaxed">{prompt}</pre>
        </div>

        {/* Usage Instructions */}
        <div className="bg-muted p-4 rounded-lg">
          <h3 className="text-lg font-bold mb-2 text-foreground flex items-center gap-2">
            <IconSolid name="lightbulb" className="text-yellow-500" />
            How to Use
          </h3>
          <ol className="space-y-2 text-foreground list-decimal list-inside">
            <li>Copy the prompt above using the "Copy" button</li>
            <li>Create a new file in your GitHub repository's <code className="bg-muted px-2 py-1 rounded text-sm">.github/chatmodes/</code> directory</li>
            <li>Paste the prompt and adjust it to your needs</li>
            <li>Commit and push to activate the chatmode</li>
          </ol>
        </div>
      </div>
    </Modal>
  )
}
